import React, { useState, useEffect } from 'react';
import { locationAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  Modal,
  Input,
  ErrorMessage,
  SuccessMessage,
  Badge,
  Pagination,
} from '../../UI';
import SearchInput from '../../UI/SearchInput';
import { SearchStatus, SearchResultsSkeleton } from '../../UI/SearchLoader';
import LocationForm from '../LocationForm';
import LocationBulkUpload from '../LocationBulkUpload';
import { MapPin, Plus, Eye, Edit, Trash2, Upload, ToggleLeft, ToggleRight } from 'lucide-react';

const LocationsPage = () => {
  const { data: locationsData, loading, refetch } = useApi(locationAPI.getLocations, [], { skip: true });
  const { data: statsData, loading: statsLoading } = useApi(locationAPI.getStats, []);
  
  const { loading: createLoading, execute: executeCreate } = useAsyncAction();
  const { loading: editLoading, execute: executeEdit } = useAsyncAction();
  const { loading: deleteLoading, execute: executeDelete } = useAsyncAction();
  const { loading: toggleLoading, execute: executeToggle } = useAsyncAction();
  
  const locations = locationsData?.locations || [];
  const stats = statsData || {};
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (search) {
      const timer = setTimeout(() => {
        setSearchLoading(true);
        refetch({ search }).finally(() => setSearchLoading(false));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      refetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleCreate = async (data) => {
    const result = await executeCreate(locationAPI.createLocation, data);
    if (result.success) {
      setSuccess('Location created');
      setIsAddModalOpen(false);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to create');
    }
  };

  const handleEdit = async (data) => {
    const result = await executeEdit(locationAPI.updateLocation, selected._id, data);
    if (result.success) {
      setSuccess('Location updated');
      setIsEditModalOpen(false);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    const result = await executeDelete(locationAPI.deleteLocation, selected._id);
    if (result.success) {
      setSuccess('Location deleted');
      setIsDeleteModalOpen(false);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete');
    }
  };

  const handleToggleStatus = async (id) => {
    const result = await executeToggle(locationAPI.toggleLocationStatus, id);
    if (result.success) {
      setSuccess('Status updated');
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to toggle');
    }
  };

  const handleBulkUploadSuccess = () => {
    setIsBulkUploadModalOpen(false);
    setSuccess('Locations uploaded successfully');
    refetch();
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Locations</h2>
          <p className="text-gray-500">Manage business locations</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setIsBulkUploadModalOpen(true)} variant="outline" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Bulk Upload
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Locations</p>
          <p className="text-2xl font-bold text-gray-900">{statsLoading ? '...' : stats.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{statsLoading ? '...' : stats.activeCount || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Inactive</p>
          <p className="text-2xl font-bold text-red-600">{statsLoading ? '...' : (stats.total - stats.activeCount) || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Revenue Divisions</p>
          <p className="text-2xl font-bold text-blue-600">{statsLoading ? '...' : stats.uniqueDivisions || 0}</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <SearchInput
          placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          isLoading={searchLoading}
          className="max-w-md"
        />
        <SearchStatus isSearching={searchLoading} resultCount={locations.length} searchTerm={search} />
      </div>

      {/* Table */}
      {loading ? (
        <SearchResultsSkeleton rows={5} />
      ) : locations.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue Division</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {locations.map((loc) => (
                  <tr key={loc._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{loc.name}</p>
                          <p className="text-sm text-gray-500">{loc.revenueVillageName || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-600">{loc.code || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{loc.revenueDivision || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{loc.mandal || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={loc.isActive !== false ? 'success' : 'secondary'}>
                          {loc.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                        <button
                          onClick={() => handleToggleStatus(loc._id)}
                          disabled={toggleLoading}
                          className="p-1 rounded hover:bg-gray-100"
                        >
                          {loc.isActive !== false ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setSelected(loc); setIsViewModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100">
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button onClick={() => { setSelected(loc); setIsEditModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100">
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button onClick={() => { setSelected(loc); setIsDeleteModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {locationsData?.pagination?.pages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                currentPage={locationsData.pagination.page}
                totalPages={locationsData.pagination.pages}
                onPageChange={(page) => refetch({ page })}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No locations found</h3>
          <p className="text-gray-500 mb-4">{search ? `No results for "${search}"` : 'Add your first location'}</p>
          <Button onClick={() => setIsAddModalOpen(true)}>Add Location</Button>
        </div>
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Location" size="lg">
        <LocationForm
          onSubmit={handleCreate}
          onCancel={() => setIsAddModalOpen(false)}
          loading={createLoading}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Location" size="lg">
        <LocationForm
          location={selected}
          onSubmit={handleEdit}
          onCancel={() => setIsEditModalOpen(false)}
          loading={editLoading}
        />
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Location Details" size="lg">
        {selected && (
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-gray-500">Name</p><p className="font-medium">{selected.name}</p></div>
            <div><p className="text-sm text-gray-500">Code</p><p className="font-medium">{selected.code || 'N/A'}</p></div>
            <div><p className="text-sm text-gray-500">Revenue Division</p><p className="font-medium">{selected.revenueDivision || 'N/A'}</p></div>
            <div><p className="text-sm text-gray-500">Mandal</p><p className="font-medium">{selected.mandal || 'N/A'}</p></div>
            <div><p className="text-sm text-gray-500">Revenue Village</p><p className="font-medium">{selected.revenueVillageName || 'N/A'}</p></div>
            <div><p className="text-sm text-gray-500">Status</p><Badge variant={selected.isActive !== false ? 'success' : 'secondary'}>{selected.isActive !== false ? 'Active' : 'Inactive'}</Badge></div>
            {selected.description && (
              <div className="col-span-2"><p className="text-sm text-gray-500">Description</p><p className="font-medium">{selected.description}</p></div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Location" size="sm">
        <div className="text-center">
          <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Delete <strong>{selected?.name}</strong>?</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal isOpen={isBulkUploadModalOpen} onClose={() => setIsBulkUploadModalOpen(false)} title="Bulk Upload Locations" size="xl">
        <LocationBulkUpload onSuccess={handleBulkUploadSuccess} onClose={() => setIsBulkUploadModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default LocationsPage;
